import React, { useState, useEffect } from "react";
import { Modal, Button, Badge, Form, Alert } from "react-bootstrap";
import useToken from "../Components/useToken";
import Pagination from "../Components/Pagination";
import { getReceiptLogs, getReceiptLogById } from "../Common/utils";

const ReceiptLogs = () => {
  const [logs, setLogs] = useState({ data: [], pagination: {}, statistics: {} });
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState({ status: "", booking_id: "", from_date: "", to_date: "" });
  const [selectedLog, setSelectedLog] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [loader, setLoader] = useState(false);
  const { token } = useToken();

  // Fetch logs
  const fetchLogs = async () => {
    setLoader(true);
    try {
      const cleanFilter = Object.fromEntries(
        Object.entries(filter).filter(([_, v]) => v !== "")
      );
      const result = await getReceiptLogs(token, page, cleanFilter);
      setLogs(result);
    } catch (error) {
      console.error("Errore nel caricamento dei log:", error);
    }
    setLoader(false);
  };

  useEffect(() => {
    fetchLogs();
  }, [page]);

  // Handle filter change
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilter({ ...filter, [name]: value });
  };

  // Handle filter submit
  const handleFilterSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    fetchLogs();
  };

  // Handle clear filters
  const handleClearFilters = () => {
    setFilter({ status: "", booking_id: "", from_date: "", to_date: "" });
    setPage(1);
    setTimeout(() => fetchLogs(), 100);
  };

  // Open detail modal
  const handleShowDetail = async (logId) => {
    try {
      const log = await getReceiptLogById(token, logId);
      setSelectedLog(log);
      setShowDetailModal(true);
    } catch (error) {
      console.error("Errore nel caricamento del dettaglio:", error);
    }
  };

  // Get status badge
  const getStatusBadge = (status) => {
    switch (status) {
      case "success":
        return <Badge bg="success">✓ Successo</Badge>;
      case "error":
        return <Badge bg="danger">✗ Errore</Badge>;
      case "pending":
        return <Badge bg="warning" text="dark">⏳ In Attesa</Badge>;
      default:
        return <Badge bg="secondary">{status}</Badge>;
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString("it-IT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  // Format amount
  const formatAmount = (amount) => {
    return parseFloat(amount).toFixed(2) + " €";
  };

  // Statistics panel
  const StatisticsPanel = () => {
    const stats = logs.statistics || {};
    const successRate = stats.total_logs > 0
      ? ((stats.success_count / stats.total_logs) * 100).toFixed(1)
      : 0;

    return (
      <div className="row mb-4">
        <div className="col-md-3">
          <div className="card text-center" style={{ backgroundColor: "#f8f9fa", border: "none", boxShadow: "0px 2px 4px rgba(0,0,0,0.1)" }}>
            <div className="card-body">
              <h6 className="card-title text-muted">Totale Invii</h6>
              <h2 className="card-text">{stats.total_logs || 0}</h2>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card text-center" style={{ backgroundColor: "#d4edda", border: "none", boxShadow: "0px 2px 4px rgba(0,0,0,0.1)" }}>
            <div className="card-body">
              <h6 className="card-title text-success">✓ Successi</h6>
              <h2 className="card-text text-success">{stats.success_count || 0}</h2>
              <p className="card-text text-success mb-0" style={{ fontSize: "0.9em" }}>
                {formatAmount(stats.total_amount_success || 0)}
              </p>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card text-center" style={{ backgroundColor: "#f8d7da", border: "none", boxShadow: "0px 2px 4px rgba(0,0,0,0.1)" }}>
            <div className="card-body">
              <h6 className="card-title text-danger">✗ Errori</h6>
              <h2 className="card-text text-danger">{stats.error_count || 0}</h2>
              <p className="card-text text-danger mb-0" style={{ fontSize: "0.9em" }}>
                {formatAmount(stats.total_amount_error || 0)}
              </p>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card text-center" style={{ backgroundColor: "#fff3cd", border: "none", boxShadow: "0px 2px 4px rgba(0,0,0,0.1)" }}>
            <div className="card-body">
              <h6 className="card-title text-warning">Tasso Successo</h6>
              <h2 className="card-text text-warning">{successRate}%</h2>
              <p className="card-text text-warning mb-0" style={{ fontSize: "0.9em" }}>
                ⏳ Pending: {stats.pending_count || 0}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={{ width: "100%" }}>
      {/* Header */}
      <div
        style={{
          paddingTop: "65px",
          width: "100%",
          backgroundColor: "#F4F4F4",
        }}
      >
        <h1 className="title-sezione">
          <img
            src="prenotazioniIcon.svg"
            style={{
              position: "relative",
              bottom: "3px",
              width: "23px",
              marginRight: "12px",
            }}
            alt=""
          />
          Log Agenzia delle Entrate
        </h1>

        {/* Filter Form */}
        <Form onSubmit={handleFilterSubmit} style={{ marginTop: "30px", width: "75%" }}>
          <div className="row g-3">
            <div className="col-md-2">
              <Form.Select
                name="status"
                value={filter.status}
                onChange={handleFilterChange}
                style={{
                  borderRadius: "25px",
                  height: "50px",
                  boxShadow: "0px 2px 4px #00000029",
                  border: "1px solid #ddd"
                }}
              >
                <option value="">Tutti gli stati</option>
                <option value="success">✓ Successo</option>
                <option value="error">✗ Errore</option>
                <option value="pending">⏳ In Attesa</option>
              </Form.Select>
            </div>
            <div className="col-md-2">
              <Form.Control
                type="text"
                name="booking_id"
                placeholder="ID Prenotazione"
                value={filter.booking_id}
                onChange={handleFilterChange}
                style={{
                  borderRadius: "25px",
                  height: "50px",
                  boxShadow: "0px 2px 4px #00000029",
                  border: "1px solid #ddd"
                }}
              />
            </div>
            <div className="col-md-2">
              <Form.Control
                type="date"
                name="from_date"
                placeholder="Da data"
                value={filter.from_date}
                onChange={handleFilterChange}
                style={{
                  borderRadius: "25px",
                  height: "50px",
                  boxShadow: "0px 2px 4px #00000029",
                  border: "1px solid #ddd"
                }}
              />
            </div>
            <div className="col-md-2">
              <Form.Control
                type="date"
                name="to_date"
                placeholder="A data"
                value={filter.to_date}
                onChange={handleFilterChange}
                style={{
                  borderRadius: "25px",
                  height: "50px",
                  boxShadow: "0px 2px 4px #00000029",
                  border: "1px solid #ddd"
                }}
              />
            </div>
            <div className="col-md-2">
              <Button
                type="submit"
                style={{
                  borderRadius: "25px",
                  height: "50px",
                  width: "100%",
                  boxShadow: "0px 2px 4px #00000029",
                  backgroundColor: "#9b3601",
                  border: "none"
                }}
              >
                Filtra
              </Button>
            </div>
            <div className="col-md-2">
              <Button
                variant="outline-secondary"
                onClick={handleClearFilters}
                style={{
                  borderRadius: "25px",
                  height: "50px",
                  width: "100%",
                  boxShadow: "0px 2px 4px #00000029"
                }}
              >
                Pulisci
              </Button>
            </div>
          </div>
        </Form>
      </div>

      {/* Main Content */}
      <div style={{ marginTop: "30px" }}>
        {/* Statistics */}
        <StatisticsPanel />

        {/* Table */}
        <div className="card" style={{ border: "none", boxShadow: "0px 3px 6px #00000029", borderRadius: "10px" }}>
          <div className="card-body" style={{ padding: "0" }}>
            {loader ? (
              <div className="text-center p-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Caricamento...</span>
                </div>
              </div>
            ) : logs.data && logs.data.length > 0 ? (
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead style={{ backgroundColor: "#f8f9fa", borderBottom: "2px solid #dee2e6" }}>
                    <tr>
                      <th style={{ padding: "15px" }}>ID</th>
                      <th>Booking</th>
                      <th>Cliente</th>
                      <th>Importo</th>
                      <th>Status</th>
                      <th>Receipt ID</th>
                      <th>Doc. Number</th>
                      <th>Data</th>
                      <th>Azioni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.data.map((log) => (
                      <tr key={log.id}>
                        <td style={{ padding: "15px" }}>{log.id}</td>
                        <td>
                          <strong>ORD-{log.booking_id}</strong>
                        </td>
                        <td>
                          {log.cliente_nome && log.cliente_cognome ? (
                            <>
                              {log.cliente_nome} {log.cliente_cognome}
                              <br />
                              <small className="text-muted">{log.cliente_email}</small>
                            </>
                          ) : (
                            <span className="text-muted">N/A</span>
                          )}
                        </td>
                        <td>{formatAmount(log.total_amount)}</td>
                        <td>{getStatusBadge(log.status)}</td>
                        <td>
                          {log.receipt_id ? (
                            <code style={{ fontSize: "0.85em" }}>{log.receipt_id.substring(0, 12)}...</code>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                        <td>
                          {log.document_number || <span className="text-muted">-</span>}
                        </td>
                        <td style={{ fontSize: "0.9em" }}>
                          {formatDate(log.created_at)}
                        </td>
                        <td>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleShowDetail(log.id)}
                            style={{ borderRadius: "15px" }}
                          >
                            Dettagli
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center p-5">
                <Alert variant="info">
                  Nessun log trovato con i filtri selezionati.
                </Alert>
              </div>
            )}
          </div>
        </div>

        {/* Pagination */}
        <div style={{ width: "100%", display: "flex", justifyContent: "center", marginTop: "20px" }}>
          <Pagination page={page} setPage={setPage} pagination={logs.pagination} />
        </div>
      </div>

      {/* Detail Modal */}
      <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Dettaglio Log ID: {selectedLog?.id}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedLog && (
            <div>
              <div className="row mb-3">
                <div className="col-md-6">
                  <strong>Booking ID:</strong> ORD-{selectedLog.booking_id}
                </div>
                <div className="col-md-6">
                  <strong>Status:</strong> {getStatusBadge(selectedLog.status)}
                </div>
              </div>
              <div className="row mb-3">
                <div className="col-md-6">
                  <strong>Receipt ID:</strong> {selectedLog.receipt_id || "N/A"}
                </div>
                <div className="col-md-6">
                  <strong>Document Number:</strong> {selectedLog.document_number || "N/A"}
                </div>
              </div>
              <div className="row mb-3">
                <div className="col-md-6">
                  <strong>Fiscal ID:</strong> {selectedLog.fiscal_id}
                </div>
                <div className="col-md-6">
                  <strong>Importo:</strong> {formatAmount(selectedLog.total_amount)}
                </div>
              </div>
              <div className="row mb-3">
                <div className="col-md-6">
                  <strong>OpenAPI Status:</strong> {selectedLog.openapi_status || "N/A"}
                </div>
                <div className="col-md-6">
                  <strong>Tentativo N°:</strong> {selectedLog.attempt_number}
                </div>
              </div>
              <div className="row mb-3">
                <div className="col-md-6">
                  <strong>Creato:</strong> {formatDate(selectedLog.created_at)}
                </div>
                <div className="col-md-6">
                  <strong>Aggiornato:</strong> {formatDate(selectedLog.updated_at)}
                </div>
              </div>

              {selectedLog.error_message && (
                <Alert variant="danger" className="mt-3">
                  <strong>Errore:</strong> {selectedLog.error_message}
                </Alert>
              )}

              {selectedLog.request_payload && (
                <div className="mt-3">
                  <h6>Request Payload:</h6>
                  <pre style={{ backgroundColor: "#f8f9fa", padding: "10px", borderRadius: "5px", fontSize: "0.85em", maxHeight: "200px", overflow: "auto" }}>
                    {JSON.stringify(
                      typeof selectedLog.request_payload === 'string'
                        ? JSON.parse(selectedLog.request_payload)
                        : selectedLog.request_payload,
                      null,
                      2
                    )}
                  </pre>
                </div>
              )}

              {selectedLog.response_payload && (
                <div className="mt-3">
                  <h6>Response Payload:</h6>
                  <pre style={{ backgroundColor: "#f8f9fa", padding: "10px", borderRadius: "5px", fontSize: "0.85em", maxHeight: "200px", overflow: "auto" }}>
                    {JSON.stringify(
                      typeof selectedLog.response_payload === 'string'
                        ? JSON.parse(selectedLog.response_payload)
                        : selectedLog.response_payload,
                      null,
                      2
                    )}
                  </pre>
                </div>
              )}

              {selectedLog.error_stack && (
                <div className="mt-3">
                  <h6>Stack Trace:</h6>
                  <pre style={{ backgroundColor: "#f8f9fa", padding: "10px", borderRadius: "5px", fontSize: "0.75em", maxHeight: "200px", overflow: "auto" }}>
                    {selectedLog.error_stack}
                  </pre>
                </div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetailModal(false)}>
            Chiudi
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ReceiptLogs;
